import React, { useState, useEffect, useCallback } from 'react';
import { projectAPI } from '../services/api.ts';
import { Layout } from '../components/Layout.tsx';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  NoSymbolIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  FolderIcon,
} from '@heroicons/react/24/outline';
import { ProjectForm } from '../components/ProjectForm.tsx';
import { Toast } from '../components/Toast.tsx';

export const Projects: React.FC = () => {
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentProject, setCurrentProject] = useState<Partial<any> | null>(
    null,
  );
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [successMessage, setSuccessMessage] = useState('');
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<any | null>(null);
  const [totalItems, setTotalItems] = useState(0);

  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [loadingStatus, setLoadingStatus] = useState<number | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    projectId: string | null;
  }>({
    isOpen: false,
    projectId: null,
  });
  const [statusConfirmation, setStatusConfirmation] = useState<{
    isOpen: boolean;
    projectId: number | null;
    currentStatus: number;
  }>({
    isOpen: false,
    projectId: null,
    currentStatus: 0,
  });

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await projectAPI.getAll(page);
      if (response.data) {
        setProjects(response.data.projects);
        setTotalPages(response.data.pagination.pages);
        setTotalItems(response.data.pagination.total);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchProjects();
  }, [page, fetchProjects]);

  const handleSubmit = async (projectData: any) => {
    try {
      const projectId = currentProject?.id;

      let response;
      if (projectId) {
        // Update existing project
        response = await projectAPI.update(projectId, projectData);
      } else {
        // Create new project
        response = await projectAPI.create(projectData);
      }

      if (response.success) {
        setToast({ message: response.message, type: 'success' });
        setIsModalOpen(false);
        fetchProjects();
      } else {
        // throw new Error(response.message || 'Operation failed');
      }
    } catch (error: any) {
      setToast({
        message:
          error.response?.data?.message || error.message || 'Operation failed',
        type: 'error',
      });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      setError('');
      setSuccessMessage('');

      const response = await projectAPI.delete(id);

      if (response.success) {
        setSuccessMessage(response.message);
        fetchProjects();
      } else {
        setError(response.message);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete project');
    } finally {
      setDeleteConfirmation({ isOpen: false, projectId: null });
    }
  };

  const handleImageError = (
    e: React.SyntheticEvent<HTMLImageElement, Event>,
  ) => {
    e.currentTarget.src = '/default.png';
    e.currentTarget.onerror = null; // Prevents infinite loop if default image also fails
  };

  const handleToggleStatus = async (id: number) => {
    try {
      setLoadingStatus(id);
      setError('');
      const response = await projectAPI.toggleStatus(id);
      if (response.success) {
        setToast({ message: response.message, type: 'success' });
        fetchProjects();
      }
    } catch (err: any) {
      setToast({ message: err.message, type: 'error' });
    } finally {
      setLoadingStatus(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary-200 dark:border-primary-800"></div>
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-primary-600 dark:border-primary-400 absolute top-0 left-0"></div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="py-8 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg shadow-md">
                  <FolderIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                    Projects
                  </h1>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                    Manage your project portfolio
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setCurrentProject({});
                  setIsModalOpen(true);
                }}
                className="flex items-center gap-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white px-6 py-3 rounded-xl font-medium hover:from-primary-600 hover:to-primary-700 transition-all shadow-md hover:shadow-lg"
              >
                <PlusIcon className="h-5 w-5" />
                Add Project
              </button>
            </div>

            {/* Success Message */}
            {successMessage && (
              <div className="mt-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 dark:text-green-400 mr-3" />
                  <p className="text-green-800 dark:text-green-200 font-medium text-sm flex-1">
                    {successMessage}
                  </p>
                  <button
                    type="button"
                    onClick={() => setSuccessMessage('')}
                    className="p-1 hover:bg-green-100 rounded-lg transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5 text-green-600" />
                  </button>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                <div className="flex items-center">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-500 dark:text-red-400 mr-3" />
                  <p className="text-red-800 dark:text-red-200 font-medium text-sm flex-1">
                    {error}
                  </p>
                  <button
                    type="button"
                    onClick={() => setError('')}
                    className="p-1 hover:bg-red-100 dark:hover:bg-red-800 rounded-lg transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="mt-8 flex flex-col">
            <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                {projects.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                      />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                      No projects
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Get started by adding a new project.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 dark:ring-gray-700 md:rounded-lg">
                      <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700">
                        <thead>
                          <tr className="bg-gray-200 dark:bg-gray-700">
                            <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">
                              Logo
                            </th>
                            <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">
                              Name
                            </th>
                            <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">
                              Status
                            </th>
                            <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">
                              Company
                            </th>
                            <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                              <span className="sr-only">Actions</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                          {projects.length === 0 ? (
                            <tr>
                              <td
                                colSpan={6}
                                className="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                              >
                                No projects found
                              </td>
                            </tr>
                          ) : (
                            projects.map((project) => {
                              return (
                                <tr key={project._id}>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                                    {project.logo ? (
                                      <img
                                        src={project.logo || '/default.png'}
                                        alt={`${project.name} logo`}
                                        className="h-10 w-10 object-contain"
                                        onError={handleImageError}
                                      />
                                    ) : (
                                      <div className="h-10 w-50 bg-gray-100 dark:bg-gray-700 flex items-center justify-center rounded">
                                        <span className="text-gray-400 dark:text-gray-500">
                                          No logo
                                        </span>
                                      </div>
                                    )}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 dark:text-white">
                                    {project.name}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                                    <span
                                      className={`px-2 py-1 rounded ${
                                        project.status === 1
                                          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                          : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                                      }`}
                                    >
                                      {project.status === 1
                                        ? 'Active'
                                        : 'Inactive'}
                                    </span>
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                                    {project.company?.name || 'N/A'}
                                  </td>
                                  <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                    <button
                                      onClick={() => {
                                        setSelectedProject(project);
                                        setIsViewModalOpen(true);
                                      }}
                                      className="text-primary hover:text-primary-dark mr-4"
                                    >
                                      <EyeIcon className="h-5 w-5" />
                                    </button>
                                    <button
                                      onClick={async () => {
                                        try {
                                          const response =
                                            await projectAPI.getById(
                                              project.id,
                                            );
                                          if (response.success) {
                                            setCurrentProject({
                                              ...response.data,
                                              odoo_configuration_id: '',
                                              company_id:
                                                response.data?.company_id || '',
                                            });
                                            setIsModalOpen(true);
                                          }
                                        } catch (error: any) {
                                          setToast({
                                            message: error.message,
                                            type: 'error',
                                          });
                                        }
                                      }}
                                      className="text-primary hover:text-primary-dark mr-4"
                                    >
                                      <PencilIcon className="h-5 w-5" />
                                    </button>
                                    <button
                                      onClick={() =>
                                        setStatusConfirmation({
                                          isOpen: true,
                                          projectId: project.id,
                                          currentStatus: project.status,
                                        })
                                      }
                                      className={`mr-4 ${
                                        loadingStatus === project.id
                                          ? 'opacity-50 cursor-not-allowed'
                                          : 'hover:text-primary-dark'
                                      }`}
                                      disabled={loadingStatus === project.id}
                                    >
                                      {project.status === 1 ? (
                                        <NoSymbolIcon className="h-5 w-5 text-red-600" />
                                      ) : (
                                        <CheckCircleIcon className="h-5 w-5 text-green-600" />
                                      )}
                                    </button>
                                    <button
                                      onClick={() =>
                                        setDeleteConfirmation({
                                          isOpen: true,
                                          projectId: project.id,
                                        })
                                      }
                                      className="text-red-600 hover:text-red-900"
                                    >
                                      <TrashIcon className="h-5 w-5" />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination - Only show when there are projects */}
                    <div className="mt-4 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 sm:px-6">
                      <div className="flex flex-1 justify-between sm:hidden">
                        <button
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          disabled={page === 1}
                          className="relative inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() =>
                            setPage((p) => Math.min(totalPages, p + 1))
                          }
                          disabled={page === totalPages}
                          className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                        >
                          Next
                        </button>
                      </div>
                      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm text-gray-700 dark:text-gray-300">
                            Showing{' '}
                            <span className="font-medium">
                              {Math.min((page - 1) * 10 + 1, totalItems)}
                            </span>{' '}
                            to{' '}
                            <span className="font-medium">
                              {Math.min(page * 10, totalItems)}
                            </span>{' '}
                            of <span className="font-medium">{totalItems}</span>{' '}
                            results
                          </p>
                        </div>
                        <div>
                          <nav
                            className="isolate inline-flex -space-x-px rounded-md shadow-sm"
                            aria-label="Pagination"
                          >
                            <button
                              onClick={() => setPage((p) => Math.max(1, p - 1))}
                              disabled={page === 1}
                              className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 dark:text-gray-500 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0"
                            >
                              Previous
                            </button>
                            <button
                              onClick={() =>
                                setPage((p) => Math.min(totalPages, p + 1))
                              }
                              disabled={page === totalPages}
                              className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 dark:text-gray-500 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0"
                            >
                              Next
                            </button>
                          </nav>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Project Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setIsModalOpen(false)}
            ></div>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6 max-h-[85vh]">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div
                className="sm:flex sm:items-start overflow-y-auto"
                style={{ maxHeight: 'calc(85vh - 100px)' }}
              >
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {currentProject?._id ? 'Edit Project' : 'New Project'}
                  </h3>
                  <div className="mt-4">
                    <ProjectForm
                      initialValues={{
                        _id: currentProject?._id,
                        name: currentProject?.name || '',
                        status: currentProject?.status || 1,
                        company_id: currentProject?.company?.id || '',
                        logo: currentProject?.logo || '',
                        color_scheme: {
                          primary: currentProject?.color_scheme?.primary || '',
                          secondary:
                            currentProject?.color_scheme?.secondary || '',
                        },
                        odoo: {
                          url: currentProject?.odoo?.url || '',
                          db: currentProject?.odoo?.db || '',
                          username: currentProject?.odoo?.username || '',
                          password: currentProject?.odoo?.password || '',
                        },
                        admin_email: currentProject?.admin_user?.email || '',
                        heading: currentProject?.heading || '',
                        sub_heading: currentProject?.sub_heading || '',
                        audit_department_email:
                          currentProject?.audit_department_email || '',
                      }}
                      onSubmit={handleSubmit}
                      onCancel={() => setIsModalOpen(false)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Project Details Modal */}
      {isViewModalOpen && selectedProject && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setIsViewModalOpen(false)}
            ></div>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6 max-h-[85vh]">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                {/* <button
                  onClick={() => setIsViewModalOpen(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button> */}
              </div>

              <div
                className="sm:flex sm:items-start overflow-y-auto"
                style={{ maxHeight: 'calc(85vh - 100px)' }}
              >
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Project Details
                  </h3>
                  <div className="mt-4 space-y-4">
                    <div className="space-y-2">
                      <div className="flex justify-center mb-6">
                        {selectedProject.logo ? (
                          <img
                            src={selectedProject.logo}
                            alt={`${selectedProject.name} logo`}
                            className="h-24 w-24 object-contain"
                            onError={handleImageError}
                          />
                        ) : (
                          <img
                            src={'/default.png'}
                            alt="Default logo"
                            className="h-24 w-24 object-contain"
                          />
                        )}
                      </div>
                      <div className="pl-4">
                        <p>
                          <span className="font-medium">Name:</span>{' '}
                          {selectedProject.name}
                        </p>
                        <p>
                          <span className="font-medium">Status:</span>{' '}
                          {selectedProject.status === 1 ? 'Active' : 'Inactive'}
                        </p>
                        <p>
                          <span className="font-medium">Admin Email:</span>{' '}
                          {selectedProject.admin_user?.email || 'N/A'}
                        </p>
                        <p>
                          <span className="font-medium">Company:</span>{' '}
                          {selectedProject.company?.name || 'N/A'}
                        </p>
                        <p>
                          <span className="font-medium">Heading:</span>{' '}
                          {selectedProject.heading || 'N/A'}
                        </p>
                        <p>
                          <span className="font-medium">Sub Heading:</span>{' '}
                          {selectedProject.sub_heading || 'N/A'}
                        </p>
                      </div>
                      <hr />
                      {/* Odoo Configuration */}
                      <div>
                        <h4 className="font-medium mb-2">
                          Odoo Configuration:
                        </h4>
                        <div className="pl-4">
                          <p>
                            <span className="font-medium">URL:</span>{' '}
                            {selectedProject.odoo?.url || 'N/A'}
                          </p>
                          <p>
                            <span className="font-medium">Database:</span>{' '}
                            {selectedProject.odoo?.db || 'N/A'}
                          </p>
                          <p>
                            <span className="font-medium">Username:</span>{' '}
                            {selectedProject.odoo?.username || 'N/A'}
                          </p>
                          {/* <p><span className="font-medium">Password:</span> {selectedProject.odoo?.password || 'N/A'}</p> */}
                        </div>
                      </div>
                      <hr />
                      {/* Color Scheme */}
                      <div>
                        <h4 className="font-medium mb-2">Color Scheme:</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm">Primary:</p>
                            <div className="flex items-center space-x-2">
                              <div
                                className="w-6 h-6 rounded border"
                                style={{
                                  backgroundColor:
                                    selectedProject.color_scheme?.primary,
                                }}
                              />
                              <span>
                                {selectedProject.color_scheme?.primary || 'N/A'}
                              </span>
                            </div>
                          </div>
                          <div>
                            <p className="text-sm">Secondary:</p>
                            <div className="flex items-center space-x-2">
                              <div
                                className="w-6 h-6 rounded border"
                                style={{
                                  backgroundColor:
                                    selectedProject.color_scheme?.secondary,
                                }}
                              />
                              <span>
                                {selectedProject.color_scheme?.secondary ||
                                  'N/A'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Close Button */}
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse sticky bottom-0 bg-white pt-4 border-t">
                    <button
                      type="button"
                      onClick={() => setIsViewModalOpen(false)}
                      className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {deleteConfirmation.isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() =>
                setDeleteConfirmation({ isOpen: false, projectId: null })
              }
            ></div>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <svg
                    className="h-6 w-6 text-red-600"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Delete Project
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete this project? This action
                      cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => {
                    if (deleteConfirmation.projectId) {
                      handleDelete(deleteConfirmation.projectId);
                    }
                  }}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:w-auto sm:text-sm"
                  onClick={() =>
                    setDeleteConfirmation({ isOpen: false, projectId: null })
                  }
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {statusConfirmation.isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() =>
                setStatusConfirmation({
                  isOpen: false,
                  projectId: null,
                  currentStatus: 0,
                })
              }
            ></div>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6 max-h-[85vh]">
              <div
                className="sm:flex sm:items-start overflow-y-auto"
                style={{ maxHeight: 'calc(85vh - 100px)' }}
              >
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                  <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {statusConfirmation.currentStatus === 1
                      ? 'Deactivate'
                      : 'Activate'}{' '}
                    Project
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to{' '}
                      {statusConfirmation.currentStatus === 1
                        ? 'deactivate'
                        : 'activate'}{' '}
                      this project?
                      {statusConfirmation.currentStatus === 1
                        ? ' This will prevent users from accessing the project.'
                        : ' This will allow users to access the project.'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse sticky bottom-0 bg-white pt-4 border-t">
                <button
                  type="button"
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm ${
                    statusConfirmation.currentStatus === 1
                      ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                      : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                  }`}
                  onClick={() => {
                    if (statusConfirmation.projectId) {
                      handleToggleStatus(statusConfirmation.projectId);
                      setStatusConfirmation({
                        isOpen: false,
                        projectId: null,
                        currentStatus: 0,
                      });
                    }
                  }}
                >
                  {statusConfirmation.currentStatus === 1
                    ? 'Deactivate'
                    : 'Activate'}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:w-auto sm:text-sm"
                  onClick={() =>
                    setStatusConfirmation({
                      isOpen: false,
                      projectId: null,
                      currentStatus: 0,
                    })
                  }
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};
